import React, { useState, useEffect } from "react";
import { Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  LineElement,
  PointElement,
  CategoryScale,
  LinearScale,
} from "chart.js";
import "./MultiQubitEchoDemo.css";

ChartJS.register(LineElement, PointElement, CategoryScale, LinearScale);

// Random small phase noise
const randomPhase = (amp = 0.3) => (Math.random() - 0.5) * amp;

// Mini Bloch qubit with glow and bounded rephasing
function MiniBloch({ theta, phi, echo, active }) {
  // keep dots within circle always by limiting radius
  const maxRadius = 28; // slightly less than circle radius
  const x = Math.sin(theta) * Math.cos(phi) * maxRadius;
  const z = Math.cos(theta) * maxRadius;
  const cx = 40 + x;
  const cy = 40 - z;

  const color = echo ? "#00ffcc" : active ? "#ffaa00" : "#666";

  return (
    <svg viewBox="0 0 80 80" width="80" height="80" className="mini-bloch">
      <circle cx="40" cy="40" r="30" fill="none" stroke="#333" strokeWidth="1" />
      <circle
        cx={cx}
        cy={cy}
        r="4"
        fill={color}
        style={{
          filter: echo ? "drop-shadow(0 0 12px #00ffcc)" : "",
          transition: "all 0.5s ease",
        }}
      />
    </svg>
  );
}

export default function MultiQubitEchoDemo() {
  const GRID_ROWS = 5;
  const GRID_COLS = 8;
  const TOTAL = GRID_ROWS * GRID_COLS;

  const [thetas, setThetas] = useState(Array(TOTAL).fill(Math.PI / 4));
  const [phis, setPhis] = useState(Array(TOTAL).fill(0));
  const [fidelity, setFidelity] = useState(1);
  const [step, setStep] = useState(0);
  const [echoPhase, setEchoPhase] = useState(false);
  const [fidelityHistory, setFidelityHistory] = useState([1]);

  // simulation logic per step
  useEffect(() => {
    let newThetas = [...thetas];
    let newPhis = [...phis];
    let newFid = fidelity;
    let newEcho = false;

    switch (step) {
      case 0:
        newThetas = Array(TOTAL).fill(Math.PI / 4);
        newPhis = Array(TOTAL).fill(0);
        newFid = 0.95;
        break;
      case 1:
        newThetas = newThetas.map((t) => t + randomPhase(0.6));
        newPhis = newPhis.map((p) => p + randomPhase(1.5));
        newFid = 0.6;
        break;
      case 2:
        newThetas = newThetas.map((t) => Math.PI - t);
        newPhis = newPhis.map((p) => -p);
        newFid = 0.85;
        newEcho = true;
        break;
      case 3:
        // bound theta & phi to stay inside circle
        newThetas = newThetas.map(() => Math.PI / 4);
        newPhis = newPhis.map(() => 0);
        newFid = 0.97;
        newEcho = true; // highlight rephasing
        break;
      default:
        break;
    }

    setThetas(newThetas);
    setPhis(newPhis);
    setFidelity(newFid);
    setEchoPhase(newEcho);
    setFidelityHistory((prev) => [...prev.slice(-19), newFid]);
  }, [step]);

  const nextStep = () => setStep((s) => (s + 1) % 4);

  const chartData = {
    labels: fidelityHistory.map((_, i) => `T${i}`),
    datasets: [
      {
        label: "Fidelity",
        data: fidelityHistory,
        fill: false,
        borderColor: "#00ffcc",
        tension: 0.3,
      },
    ],
  };

  const rowFidelity = Array.from({ length: GRID_ROWS }).map((_, r) => {
    const rowVals = thetas.slice(r * GRID_COLS, (r + 1) * GRID_COLS);
    return (rowVals.reduce((a, t) => a + Math.cos(t), 0) / GRID_COLS).toFixed(2);
  });

  return (
    <div className="multi-echo-root">
      <h2>🌐 Multi-Qubit Echo Coupling Simulation</h2>
      <p>
        A live visualization of <strong>Google’s 40-qubit echo array</strong> — see how decoherence spreads and recovers.
      </p>

      <div className="multi-echo-panels">
        <div className="qubit-grid">
          {thetas.map((theta, i) => (
            <MiniBloch key={i} theta={theta} phi={phis[i]} echo={echoPhase} active={step !== 0} />
          ))}
        </div>

        <div className="multi-echo-stats">
          <div className="fidelity-panel">
            <h4>System Fidelity</h4>
            <div className="fidelity-bar">
              <div
                className="fidelity-level"
                style={{
                  width: `${fidelity * 100}%`,
                  background:
                    fidelity > 0.9 ? "#00ffcc" : fidelity > 0.7 ? "#ffaa00" : "#ff3333",
                  transition: "all 0.5s ease",
                }}
              />
            </div>
            <p>{(fidelity * 100).toFixed(1)}%</p>
          </div>

          <div className="fidelity-chart">
            <Line data={chartData} options={{ scales: { y: { min: 0, max: 1 } } }} />
          </div>

          <table>
            <thead>
              <tr>
                {Array.from({ length: GRID_COLS }).map((_, c) => (
                  <th key={c}>Q{c + 1}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rowFidelity.map((f, r) => (
                <tr key={r}>
                  {Array.from({ length: GRID_COLS }).map((_, c) => (
                    <td key={c}>{f}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <button className="qr-btn" onClick={nextStep}>
        {["Initialize Qubits", "Add Noise", "Apply Echo Pulse", "Recover Coherence"][step]}
      </button>

      <p className="qr-note">
        Step {step + 1}/4 — Observe synchronized phase recovery across qubits.
      </p>
    </div>
  );
}
